/* eslint-disable no-nested-ternary */
/* eslint-disable import/no-cycle */
import axios from 'axios';
import Api from '../helpers/api';
import config from '../config';
import { getToken } from '../helpers/auth';
import { logService } from './log.service';
import { mixPanelService } from './mixpanel.service';

function getProcedures(objFilter, cancelToken) {
	// eslint-disable-next-line no-param-reassign
	cancelToken = cancelToken === undefined ? null : cancelToken;

	return Api.get('/surgical-map/v2/procedures-all', objFilter, {
		crossDomain: true,
		cancelToken,
	});
}

async function getProceduresForAttendance(objFilter, cancelToken) {
	// eslint-disable-next-line no-param-reassign
	cancelToken = cancelToken === undefined ? null : cancelToken;

	const entities = ['pres', 'procedures', 'posts'];
	const { key } = objFilter.params;
	const entity = entities[parseInt(key, 10) - 1];

	// ✅ Validar se a entidade existe
	if (!entity) {
		throw new Error('Entidade inválida');
	}

	const isPre = entity === 'pres';
	const isPost = entity === 'posts';

	// ✅ Criar novo objeto para evitar mutação
	const filterParams = {
		...objFilter.params,
		isPre,
		isPost,
	};

	const proceduresPromise = Api.get(
		'/app/v4/procedures-attendances',
		{ params: filterParams },
		{ crossDomain: true, cancelToken },
	);

	const countPromise = Api.get(
		'/app/v4/procedures-attendances/count',
		{ params: filterParams },
		{ crossDomain: true, cancelToken },
	);

	const [procedures, count] = await Promise.all([
		proceduresPromise,
		countPromise,
	]);

	return {
		procedures: procedures || [],
		count: count && count.total ? count.total : 0,
	};
}

function putProcedureGroup(procedure) {
	if (!procedure || !procedure.id) {
		throw new Error('ID do procedimento é obrigatório');
	}
	return Api.put(`/preset/procedures/${procedure.id}/update-group`, procedure);
}

async function getViewPdf(pdf) {
	// ✅ Validar parâmetro
	if (!pdf) {
		throw new Error('ID do PDF é obrigatório');
	}

	const token = getToken();

	if (!token) {
		throw new Error('Token de autenticação não encontrado');
	}

	try {
		const response = await axios({
			url: `${config.urlApi}/surgical-map/v2/procedure-download/${pdf}`,
			method: 'GET',
			responseType: 'blob',
			headers: {
				Authorization: token,
			},
			timeout: 30000,
		});

		// ✅ Validar resposta
		if (!response.data) {
			throw new Error('Resposta vazia do servidor');
		}

		const url = window.URL.createObjectURL(new Blob([response.data]));
		return { url };
	} catch (error) {
		// ✅ Melhor tratamento de erro
		if (error.response && error.response.status === 404) {
			throw new Error('PDF não encontrado');
		} else if (error.response && error.response.status === 401) {
			throw new Error('Não autorizado para acessar este PDF');
		} else {
			throw new Error('Erro ao buscar o PDF');
		}
	}
}

async function getDownload(pdf) {
	// ✅ Validar parâmetro
	if (!pdf) {
		throw new Error('ID do PDF é obrigatório');
	}

	const token = getToken();

	if (!token) {
		throw new Error('Token de autenticação não encontrado');
	}

	const response = await axios({
		url: `${config.urlApi}/surgical-map/v2/procedure-download/${pdf}`,
		method: 'GET',
		responseType: 'blob',
		headers: {
			Authorization: token,
		},
		timeout: 30000,
	});

	// ✅ Validar resposta
	if (!response.data) {
		throw new Error('Resposta vazia do servidor');
	}

	const url = window.URL.createObjectURL(new Blob([response.data]));
	const link = document.createElement('a');
	link.href = url;
	link.setAttribute('download', 'paciente-atendimento.pdf');
	document.body.appendChild(link);
	link.click();

	// ✅ Limpar recursos
	document.body.removeChild(link);
	window.URL.revokeObjectURL(url);
}

async function toggleStatus(procedureId, reason = null) {
	return Api.put(`/app/v7/procedures/${procedureId}/toggle-status`, { reason });
}

async function downloadPdfs(pdfs) {
	// ✅ Validar parâmetros
	if (!pdfs || !Array.isArray(pdfs) || pdfs.length === 0) {
		throw new Error('Lista de PDFs é obrigatória');
	}

	const token = getToken();

	if (!token) {
		throw new Error('Token de autenticação não encontrado');
	}

	const response = await axios({
		url: `${config.urlApi}/surgical-map/v3/procedure-download?pdfs=${pdfs.join(
			',',
		)}`,
		method: 'GET',
		responseType: 'blob',
		headers: {
			Authorization: token,
		},
		timeout: 60000, // ✅ Timeout maior para múltiplos PDFs
	});

	const url = window.URL.createObjectURL(new Blob([response.data]));
	const link = document.createElement('a');
	link.href = url;
	link.setAttribute('download', 'pdfs.zip');
	document.body.appendChild(link);
	link.click();

	// ✅ Limpar recursos
	document.body.removeChild(link);
	window.URL.revokeObjectURL(url);
}

async function downloadAttachment(attachmentUuid, filename) {
	// ✅ Validar parâmetros
	if (!attachmentUuid) {
		throw new Error('UUID do anexo é obrigatório');
	}

	if (!filename) {
		throw new Error('Nome do arquivo é obrigatório');
	}

	const token = getToken();

	if (!token) {
		throw new Error('Token de autenticação não encontrado');
	}

	const response = await axios({
		url: `${config.urlApi}/preset/v1/procedures-attachments/${attachmentUuid}/download`,
		method: 'GET',
		responseType: 'blob',
		headers: {
			Authorization: token,
		},
		timeout: 30000,
	});

	const url = window.URL.createObjectURL(new Blob([response.data]));
	const link = document.createElement('a');
	link.href = url;
	link.setAttribute('download', filename);
	document.body.appendChild(link);
	link.click();

	// ✅ Limpar recursos
	document.body.removeChild(link);
	window.URL.revokeObjectURL(url);
}

function uploadAttachment(procedureId, formData) {
	// ✅ Validar parâmetros
	if (!procedureId) {
		throw new Error('ID do procedimento é obrigatório');
	}

	if (!formData) {
		throw new Error('Dados do arquivo são obrigatórios');
	}

	// ✅ ENDPOINT CORRETO DO BACKEND
	return Api.post(`/app/v1/procedures/${procedureId}/attachments`, formData, {
		headers: {
			'Content-Type': 'multipart/form-data',
		},
		transformRequest: [(data) => data], // Impede transformação automática
		timeout: 60000, // ✅ Timeout maior para upload
	})
		.then((response) => response.data || response)
		.catch((error) => {
			// ✅ Melhor tratamento de erro
			if (error.response && error.response.status === 413) {
				throw new Error('Arquivo muito grande');
			} else if (error.response && error.response.status === 415) {
				throw new Error('Tipo de arquivo não suportado');
			} else {
				throw new Error('Erro ao fazer upload do arquivo');
			}
		});
}

function deleteProcedureAttachment(attachmentId) {
	// ✅ Validar parâmetro
	if (!attachmentId) {
		throw new Error('ID do anexo é obrigatório');
	}

	// ✅ ENDPOINT CORRETO DO BACKEND (usando UUID)
	return Api.delete(`/app/v1/procedures-attachments/${attachmentId}`)
		.then((response) => response.data || response)
		.catch((error) => {
			// ✅ Melhor tratamento de erro
			if (error.response && error.response.status === 404) {
				throw new Error('Anexo não encontrado');
			} else if (error.response && error.response.status === 403) {
				throw new Error('Não autorizado para deletar este anexo');
			} else {
				throw new Error('Erro ao deletar o anexo');
			}
		});
}

export const proceduresService = {
	getProcedures,
	getDownload,
	putProcedureGroup,
	uploadAttachment,
	deleteProcedureAttachment,
	downloadPdfs,
	getProceduresForAttendance,
	getViewPdf,
	downloadAttachment,
	toggleStatus,
};
